import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import PageMeta from '../../../components/base/PageMeta';
import JsonLd from '../../../components/base/JsonLd';
import BrandLogo from '../../../components/base/BrandLogo';
import FloatingCtaBar from '../../../components/feature/FloatingCtaBar';

const tools = [
  {
    name: 'GlobalListings',
    slug: 'globallistings',
    logoUrl: 'https://icon.horse/icon/globallistings.com',
    tagline: 'International Real Estate Listings & Property Search',
    desc: 'GlobalListings aggregates millions of property listings across 100+ countries in one unified search platform — from residential apartments to luxury estates and commercial properties, with advanced filters and verified agent contacts.',
    tags: ['International', 'Property Search', 'Listings', 'Global'],
    rating: 4.4,
    reviews: 3820,
    price: 'Free · Agent plans available',
    priceType: 'realestate',
    link: 'https://www.globallistings.com/',
    logoIcon: 'ri-building-2-line',
    logoGrad: 'from-fuchsia-500 to-rose-500',
    badge: '',
    badgeColor: '',
  },
  {
    name: 'Tranio',
    slug: 'tranio',
    logoUrl: 'https://icon.horse/icon/tranio.com',
    tagline: 'Luxury International Property & Real Estate Investment',
    desc: 'Tranio connects high-net-worth buyers with investment-grade international properties across 70+ countries. Full-service advisory covering Golden Visa programs, legal support, and transaction management for cross-border property purchases.',
    tags: ['Luxury', 'Investment', 'Golden Visa', 'International'],
    rating: 4.6,
    reviews: 5140,
    price: 'Free for buyers',
    priceType: 'realestate',
    link: 'https://tranio.com/',
    logoIcon: 'ri-home-heart-line',
    logoGrad: 'from-rose-500 to-pink-600',
    badge: 'Top Pick',
    badgeColor: 'bg-rose-100 text-rose-700',
  },
  {
    name: 'MyREIPro',
    slug: 'myreipro',
    logoUrl: 'https://icon.horse/icon/myreipro.com',
    tagline: 'All-in-One Real Estate Investor CRM & Deal Analysis',
    desc: 'MyREIPro is a comprehensive CRM and deal analysis platform for wholesalers, flippers, and landlords. Includes automated lead follow-up, deal calculators for all investing strategies, and integrated direct mail and SMS marketing.',
    tags: ['REI CRM', 'Deal Analysis', 'Wholesaling', 'Investors'],
    rating: 4.5,
    reviews: 2680,
    price: 'Free trial · From $97/mo',
    priceType: 'tools',
    link: 'https://www.myreipro.com/?fpr=hao97',
    logoIcon: 'ri-pie-chart-2-line',
    logoGrad: 'from-violet-500 to-purple-600',
    badge: 'Popular',
    badgeColor: 'bg-violet-100 text-violet-700',
  },
  {
    name: 'Innago',
    slug: 'innago',
    logoUrl: 'https://icon.horse/icon/innago.com',
    tagline: 'Free Property Management Software for Landlords',
    desc: 'Innago is completely free property management software featuring online rent collection, digital lease signing, tenant screening, and maintenance tracking. Trusted by 50,000+ landlords managing 500,000+ rental units across the US.',
    tags: ['Property Management', 'Landlord', 'Rent Collection', 'Free'],
    rating: 4.7,
    reviews: 6920,
    price: 'Free forever',
    priceType: 'tools',
    link: 'https://innago.com/best-rental-management-software-mbl/?utm_source=Manual&utm_medium=Affiliate+Program&utm_campaign=111111111&utm_id=nhan25',
    logoIcon: 'ri-home-gear-line',
    logoGrad: 'from-sky-500 to-cyan-600',
    badge: "Editor's Choice",
    badgeColor: 'bg-sky-100 text-sky-700',
  },
  {
    name: 'PropertyMob',
    slug: 'propertymob',
    logoUrl: 'https://icon.horse/icon/propertymob.com',
    tagline: 'Real Estate Investor Training & Deal-Finding Community',
    desc: "PropertyMob offers comprehensive real estate investing education and a vibrant community covering wholesaling, fix-and-flip, BRRRR, and creative finance. Features live coaching, deal evaluation service, and an active investor network.",
    tags: ['Education', 'Community', 'Wholesaling', 'BRRRR'],
    rating: 4.4,
    reviews: 1850,
    price: 'From $37/mo',
    priceType: 'tools',
    link: 'https://lp.propertymob.com/total-access-pass-start?am_id=nhannguyen3194',
    logoIcon: 'ri-group-2-line',
    logoGrad: 'from-fuchsia-600 to-pink-600',
    badge: '',
    badgeColor: '',
  },
  {
    name: 'FundingPips',
    slug: 'fundingpips',
    logoUrl: 'https://icon.horse/icon/fundingpips.com',
    tagline: 'Next-Gen Proprietary Trading Funding & Challenges',
    desc: 'FundingPips offers prop trading challenges giving talented traders access to up to $300,000 in funded capital with up to 90% profit splits. Challenges start from just $32, supporting forex, indices, commodities, and crypto on MT4/MT5.',
    tags: ['Prop Trading', 'Funded Account', 'Forex', 'Indices'],
    rating: 4.5,
    reviews: 4210,
    price: 'Challenges from $32',
    priceType: 'proptrading',
    link: 'https://app.fundingpips.com/register?ref=79d1f045',
    logoIcon: 'ri-line-chart-line',
    logoGrad: 'from-emerald-500 to-teal-600',
    badge: '',
    badgeColor: '',
  },
  {
    name: 'RebelsFunding',
    slug: 'rebelsfunding',
    logoUrl: 'https://icon.horse/icon/rebelsfunding.com',
    tagline: 'Rebel-Spirited Prop Firm with Flexible Trading Challenges',
    desc: 'RebelsFunding offers no-time-limit prop challenges with scalping and news trading permitted. Up to 95% profit split and scaling to $1,000,000 in funded capital — designed for traders who want flexibility without compromising earnings.',
    tags: ['Prop Trading', 'Flexible Rules', 'Scalping', 'Funded Account'],
    rating: 4.4,
    reviews: 3180,
    price: 'Challenges from $39',
    priceType: 'proptrading',
    link: 'https://rebelsfunding.com/?a_box=ck4vfaqc&a_cam=3',
    logoIcon: 'ri-rocket-line',
    logoGrad: 'from-red-500 to-rose-600',
    badge: 'Hot',
    badgeColor: 'bg-red-100 text-red-700',
  },
  {
    name: 'WeMasterTrade',
    slug: 'wemastertrad',
    logoUrl: 'https://icon.horse/icon/wemastertrade.com',
    tagline: 'Community-Driven Prop Trading Education & Funding',
    desc: 'WeMasterTrade combines prop trading funding with a comprehensive education ecosystem. Mentorship programs, strategy sharing, and technical analysis courses help traders develop their edge before accessing funded accounts up to $200,000.',
    tags: ['Prop Trading', 'Education', 'Community', 'Mentorship'],
    rating: 4.3,
    reviews: 2540,
    price: 'Challenges from $49',
    priceType: 'proptrading',
    link: 'https://my.wemastertrade.com/register?ref=097284',
    logoIcon: 'ri-team-line',
    logoGrad: 'from-indigo-500 to-violet-600',
    badge: '',
    badgeColor: '',
  },
  {
    name: 'SabioTrade',
    slug: 'sabiotrade',
    logoUrl: 'https://icon.horse/icon/sabiotrade.com',
    tagline: 'AI-Enhanced Prop Trading Platform & Funding Program',
    desc: "SabioTrade integrates AI-powered market analysis tools — sentiment analysis, correlation mapping, volatility forecasting — with a comprehensive prop trading funding program. Accounts from $10K to $200K with 80% profit splits.",
    tags: ['AI Trading', 'Prop Trading', 'Funded Account', 'Forex'],
    rating: 4.4,
    reviews: 1980,
    price: 'Challenges from $59',
    priceType: 'proptrading',
    link: 'https://sabiotrade.com/?aff=796772&aff_model=revenue&afftrack=nhan25',
    logoIcon: 'ri-brain-line',
    logoGrad: 'from-amber-500 to-orange-600',
    badge: 'New',
    badgeColor: 'bg-amber-100 text-amber-700',
  },
  {
    name: 'TopTierTrader',
    slug: 'toptiertrader',
    logoUrl: 'https://icon.horse/icon/toptiertrader.com',
    tagline: 'Elite Prop Trading Challenges for Serious Traders',
    desc: 'TopTierTrader is a premium prop firm offering institutional-grade funding up to $500,000 for professional traders. Industry-leading reputation for payout reliability, professional evaluation standards, and MetaTrader/cTrader compatibility.',
    tags: ['Prop Trading', 'Elite', 'Funded Account', 'Professional'],
    rating: 4.6,
    reviews: 5670,
    price: 'Challenges from $89',
    priceType: 'proptrading',
    link: 'https://app.toptiertrader.com/buy-challenge/?referral=7f4abcdb',
    logoIcon: 'ri-medal-line',
    logoGrad: 'from-yellow-500 to-amber-600',
    badge: "Editor's Choice",
    badgeColor: 'bg-amber-100 text-amber-700',
  },
  {
    name: 'YitaHome',
    slug: 'yitahome',
    logoUrl: 'https://icon.horse/icon/yitahome.com',
    tagline: 'Premium Affordable Home Furniture & Décor',
    desc: 'YitaHome delivers stylish, high-quality home furniture at affordable prices — sofas, bedroom sets, dining tables, office chairs, and outdoor collections. Backed by 18,000+ verified 5-star reviews on Amazon with free fast shipping on most items.',
    tags: ['Home Furniture', 'Sofa', 'Bedroom', 'Amazon', 'Affordable'],
    rating: 4.7,
    reviews: 18450,
    price: 'From $59 · Free shipping',
    priceType: 'tools',
    link: 'https://yitahomeamazonsellerus.pxf.io/qWzPVO',
    logoIcon: 'ri-sofa-line',
    logoGrad: 'from-amber-400 to-orange-500',
    badge: 'Best Value',
    badgeColor: 'bg-amber-100 text-amber-700',
  },
];

const relatedCategories = [
  {
    icon: 'ri-line-chart-line',
    grad: 'from-emerald-500 to-teal-600',
    name: 'Finance – Investment',
    desc: 'Financial planning, investment tracking, and wealth management tools',
    count: 89,
    href: '/categories/finance-investment',
  },
  {
    icon: 'ri-building-line',
    grad: 'from-violet-500 to-purple-600',
    name: 'Software – SaaS – AI',
    desc: 'Cloud software, AI-powered platforms and digital productivity tools',
    count: 160,
    href: '/categories/software-saas-ai',
  },
  {
    icon: 'ri-shopping-cart-2-line',
    grad: 'from-orange-400 to-amber-500',
    name: 'Ecommerce & Shopping',
    desc: 'Online stores, marketplaces, and shopping platforms',
    count: 126,
    href: '/categories/ecommerce-shopping',
  },
];

type FilterTab = 'all' | 'realestate' | 'tools' | 'proptrading';
type SortOption = 'highest_rating' | 'most_reviews' | 'default';

const filterLabels: Record<FilterTab, string> = {
  all: 'All',
  realestate: 'Real Estate',
  tools: 'Property Tools',
  proptrading: 'Prop Trading',
};

export default function RealEstatePropertyPage() {
  const { i18n } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [showSignUp, setShowSignUp] = useState(false);
  const [signUpEmail, setSignUpEmail] = useState('');
  const [signUpPassword, setSignUpPassword] = useState('');
  const [signUpDone, setSignUpDone] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [filterTab, setFilterTab] = useState<FilterTab>('all');
  const [sortBy, setSortBy] = useState<SortOption>('highest_rating');
  const [sortOpen, setSortOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);
  const sortRef = useRef<HTMLDivElement>(null);

  const currentLang = i18n.language === 'vi' ? 'VI' : 'EN';

  const sortLabels: Record<SortOption, string> = {
    highest_rating: 'Highest rating',
    most_reviews: 'Most reviews',
    default: 'Default',
  };

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) setLangOpen(false);
      if (sortRef.current && !sortRef.current.contains(e.target as Node)) setSortOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filtered = tools
    .filter((t) => filterTab === 'all' || t.priceType === filterTab)
    .sort((a, b) => {
      if (sortBy === 'highest_rating') return b.rating - a.rating;
      if (sortBy === 'most_reviews') return b.reviews - a.reviews;
      return 0;
    });

  const renderStars = (rating: number) => {
    const full = Math.floor(rating);
    const half = rating - full >= 0.5;
    return Array.from({ length: 5 }).map((_, i) => {
      if (i < full) return <i key={i} className="ri-star-fill text-amber-400 text-xs"></i>;
      if (i === full && half) return <i key={i} className="ri-star-half-line text-amber-400 text-xs"></i>;
      return <i key={i} className="ri-star-line text-gray-300 text-xs"></i>;
    });
  };

  const siteUrl = import.meta.env.VITE_SITE_URL ?? 'https://example.com';

  const pageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'Real Estate & Property | 247digitrend',
    url: `${siteUrl}/categories/real-estate-property`,
    description: 'Discover the best real estate platforms, property management tools, and prop trading funding programs. 10 curated services with expert reviews.',
    isPartOf: { '@id': `${siteUrl}/#website` },
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
        { '@type': 'ListItem', position: 2, name: 'Categories', item: `${siteUrl}/categories` },
        { '@type': 'ListItem', position: 3, name: 'Real Estate & Property', item: `${siteUrl}/categories/real-estate-property` },
      ],
    },
  };

  return (
    <div className="min-h-screen" style={{ background: '#fdf4ff', fontFamily: "'Inter', sans-serif" }}>
      <PageMeta
        title="Best Real Estate & Property Platforms (2026) – Compare & Reviews | 247digitrend"
        description="Compare the best real estate portals, property management tools, and prop trading platforms in 2026. Reviews of Tranio, Innago, MyREIPro, FundingPips, TopTierTrader and more."
        canonical={`${siteUrl}/categories/real-estate-property`}
        keywords="real estate platforms 2026, property management software, prop trading funded accounts, Innago review, TopTierTrader review, international property search"
      />
      <JsonLd data={pageSchema} />
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap" rel="stylesheet" />

      {/* SIGN UP MODAL */}
      {showSignUp && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={() => { setShowSignUp(false); setSignUpDone(false); }}>
          <div className="relative bg-white rounded-2xl w-full max-w-sm mx-4 p-8" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => { setShowSignUp(false); setSignUpDone(false); }} className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors cursor-pointer">
              <i className="ri-close-line text-gray-600 text-base"></i>
            </button>
            {signUpDone ? (
              <div className="text-center py-4">
                <div className="w-14 h-14 flex items-center justify-center mx-auto mb-4 rounded-full bg-fuchsia-100">
                  <i className="ri-check-line text-fuchsia-600 text-2xl"></i>
                </div>
                <h3 className="text-lg font-black text-gray-900 mb-2">Registration Successful!</h3>
                <p className="text-sm text-gray-500">Welcome to 247digitrend.</p>
                <button onClick={() => { setShowSignUp(false); setSignUpDone(false); }} className="mt-6 w-full bg-gradient-to-r from-fuchsia-500 to-rose-500 text-white text-sm font-semibold py-3 rounded-full cursor-pointer hover:opacity-90 transition-opacity whitespace-nowrap">Close</button>
              </div>
            ) : (
              <>
                <div className="text-center mb-6">
                  <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3 rounded-xl bg-gradient-to-br from-fuchsia-500 to-rose-500">
                    <i className="ri-building-2-line text-white text-base"></i>
                  </div>
                  <h3 className="text-xl font-black text-gray-900 mb-1">Create Account</h3>
                  <p className="text-xs text-gray-500">Join 50K+ users of 247digitrend</p>
                </div>
                <form onSubmit={(e) => { e.preventDefault(); setSignUpDone(true); }} className="space-y-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Email</label>
                    <input type="email" required value={signUpEmail} onChange={(e) => setSignUpEmail(e.target.value)} placeholder="your@email.com" className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-xl outline-none focus:border-fuchsia-400 transition-colors" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Password</label>
                    <input type="password" required value={signUpPassword} onChange={(e) => setSignUpPassword(e.target.value)} placeholder="At least 8 characters" className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-xl outline-none focus:border-fuchsia-400 transition-colors" />
                  </div>
                  <button type="submit" className="w-full bg-gradient-to-r from-fuchsia-500 to-rose-500 text-white text-sm font-semibold py-3 rounded-full cursor-pointer hover:opacity-90 transition-opacity whitespace-nowrap mt-2">Sign Up Now</button>
                </form>
                <p className="text-xs text-center text-gray-400 mt-4">By signing up, you agree to our <a href="#" className="text-fuchsia-600 underline">Terms</a>.</p>
              </>
            )}
          </div>
        </div>
      )}

      {/* HEADER */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur-sm border-b border-gray-100' : 'bg-white border-b border-gray-100'}`}>
        <div className="mx-auto px-8 py-2 flex items-center justify-between">
          <a href="/" className="flex items-center cursor-pointer">
            <img src="https://storage.readdy-site.link/project_files/3785ac0c-31ac-4f9e-b2ab-27ad3777db2a/7544398a-500f-41a6-81c3-aa7efbf1c0d2_logo-247.png?v=04135332188f9bc8b00780c66cd5837d" alt="247digitrend" className="h-36 w-auto object-contain" />
          </a>
          <nav className="hidden md:flex items-center gap-8">
            <a href="/" className="text-[15px] font-medium text-gray-700 hover:text-fuchsia-600 transition-colors cursor-pointer">Home</a>
            <a href="/categories" className="text-[15px] font-semibold text-fuchsia-600 transition-colors cursor-pointer">Categories</a>
            <a href="/reviews" className="text-[15px] font-medium text-gray-700 hover:text-fuchsia-600 transition-colors cursor-pointer">Reviews</a>
            <a href="/comparisons" className="text-[15px] font-medium text-gray-700 hover:text-fuchsia-600 transition-colors cursor-pointer">Comparisons</a>
            <a href="/blog" className="text-[15px] font-medium text-gray-700 hover:text-fuchsia-600 transition-colors cursor-pointer">Blog</a>
            <button className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors cursor-pointer">
              <i className="ri-search-line text-gray-500 text-sm"></i>
            </button>
          </nav>
          <div className="flex items-center gap-3">
            <div className="relative" ref={langRef}>
              <button onClick={() => setLangOpen(!langOpen)} className="flex items-center gap-1.5 bg-gray-100 hover:bg-gray-200 transition-colors rounded-full px-3 py-1.5 cursor-pointer">
                <i className="ri-translate-2 text-xs text-gray-500"></i>
                <span className="text-xs font-semibold text-gray-700">{currentLang}</span>
                <i className={`ri-arrow-down-s-line text-xs text-gray-400 transition-transform ${langOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {langOpen && (
                <div className="absolute right-0 top-full mt-1.5 bg-white rounded-xl border border-gray-100 overflow-hidden w-32" style={{ boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }}>
                  {(['VI', 'EN'] as const).map((lang) => (
                    <button key={lang} onClick={() => { i18n.changeLanguage(lang.toLowerCase()); setLangOpen(false); }} className={`w-full flex items-center gap-2 px-3 py-2 text-xs font-semibold transition-colors cursor-pointer ${currentLang === lang ? 'bg-fuchsia-50 text-fuchsia-700' : 'text-gray-600 hover:bg-gray-50'}`}>
                      <span>{lang === 'VI' ? '🇻🇳' : '🇺🇸'}</span>
                      <span>{lang === 'VI' ? 'Tiếng Việt' : 'English'}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button onClick={() => setShowSignUp(true)} className="bg-gradient-to-r from-fuchsia-500 to-rose-500 text-white text-sm font-semibold px-5 py-2 rounded-full whitespace-nowrap cursor-pointer hover:opacity-90 transition-opacity">
              Sign Up
            </button>
          </div>
        </div>
      </header>

      {/* PAGE CONTENT */}
      <main className="pt-28 pb-16">
        <div className="max-w-5xl mx-auto px-6">

          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 mb-6">
            <a href="/" className="w-7 h-7 flex items-center justify-center rounded-lg bg-white border border-gray-200 hover:border-fuchsia-300 transition-colors cursor-pointer">
              <i className="ri-home-4-line text-gray-500 text-xs"></i>
            </a>
            <i className="ri-arrow-right-s-line text-gray-400 text-sm"></i>
            <a href="/categories" className="w-7 h-7 flex items-center justify-center rounded-lg bg-white border border-gray-200 hover:border-fuchsia-300 transition-colors cursor-pointer">
              <i className="ri-apps-2-line text-gray-500 text-xs"></i>
            </a>
          </nav>

          {/* Category Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-black text-gray-900 mb-2 tracking-tight">Real Estate &amp; Property</h1>
            <p className="text-sm text-gray-500 mb-4">International property portals, landlord management tools, real estate education, and proprietary trading funding platforms</p>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1.5 bg-white border border-gray-200 rounded-full px-3 py-1.5">
                <i className="ri-tools-line text-gray-500 text-xs"></i>
                <span className="text-xs font-semibold text-gray-700">{tools.length} platforms</span>
              </div>
              <div className="flex items-center gap-1.5 bg-fuchsia-50 border border-fuchsia-200 rounded-full px-3 py-1.5">
                <i className="ri-star-fill text-amber-500 text-xs"></i>
                <span className="text-xs font-semibold text-fuchsia-700">Highly rated</span>
              </div>
            </div>
          </div>

          {/* Filter + Sort Bar */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center bg-white border border-gray-200 rounded-full px-1 py-1 gap-0.5">
              {(Object.keys(filterLabels) as FilterTab[]).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setFilterTab(tab)}
                  className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                    filterTab === tab
                      ? 'bg-gradient-to-r from-fuchsia-500 to-rose-500 text-white'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {filterLabels[tab]}
                </button>
              ))}
            </div>

            <div className="relative" ref={sortRef}>
              <button
                onClick={() => setSortOpen(!sortOpen)}
                className="flex items-center gap-2 bg-white border border-gray-200 rounded-full px-4 py-2 text-xs font-semibold text-gray-700 hover:border-gray-300 transition-colors cursor-pointer whitespace-nowrap"
              >
                <span>Sort by: {sortLabels[sortBy]}</span>
                <i className={`ri-arrow-down-s-line text-gray-400 transition-transform ${sortOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {sortOpen && (
                <div className="absolute right-0 top-full mt-1.5 bg-white rounded-xl border border-gray-100 overflow-hidden w-44 z-10" style={{ boxShadow: '0 4px 16px rgba(0,0,0,0.1)' }}>
                  {(Object.entries(sortLabels) as [SortOption, string][]).map(([key, label]) => (
                    <button key={key} onClick={() => { setSortBy(key); setSortOpen(false); }} className={`w-full flex items-center gap-2 px-4 py-2.5 text-xs font-semibold transition-colors cursor-pointer text-left ${sortBy === key ? 'bg-fuchsia-50 text-fuchsia-700' : 'text-gray-600 hover:bg-gray-50'}`}>
                      {sortBy === key && <i className="ri-check-line text-fuchsia-600"></i>}
                      <span className={sortBy === key ? '' : 'ml-4'}>{label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Tools List */}
          <div className="space-y-3">
            {filtered.map((tool, i) => (
              <div key={i} className="bg-white rounded-2xl border border-gray-200 p-5 hover:border-fuchsia-200 transition-all group">
                <div className="flex items-start gap-4">
                  <BrandLogo logoUrl={tool.logoUrl} logoGrad={tool.logoGrad} logoIcon={tool.logoIcon} name={tool.name} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                          <h3 className="text-base font-bold text-gray-900">{tool.name}</h3>
                          {tool.badge && (
                            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${tool.badgeColor}`}>{tool.badge}</span>
                          )}
                        </div>
                        <p className="text-xs font-medium text-fuchsia-600 mb-1.5">{tool.tagline}</p>
                        <p className="text-xs text-gray-500 leading-relaxed line-clamp-2 mb-3">{tool.desc}</p>
                        <div className="flex flex-wrap gap-1.5 mb-3">
                          {tool.tags.map((tag, ti) => (
                            <span key={ti} className="text-xs font-medium text-gray-500 bg-gray-100 rounded-full px-2.5 py-0.5">{tag}</span>
                          ))}
                        </div>
                        <p className="text-xs text-gray-500">
                          <i className="ri-price-tag-3-line mr-1 text-gray-400"></i>
                          {tool.price}
                        </p>
                      </div>
                      <div className="flex flex-col items-end gap-3">
                        <div className="text-right">
                          <div className="flex items-center gap-1 justify-end mb-0.5">
                            <div className="flex items-center gap-0.5">{renderStars(tool.rating)}</div>
                            <span className="text-sm font-bold text-gray-800 ml-0.5">{tool.rating}</span>
                          </div>
                          <span className="text-xs text-gray-400">({tool.reviews.toLocaleString()} reviews)</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <a
                            href={`/categories/real-estate-property/${tool.slug}`}
                            className="flex items-center gap-1.5 px-3.5 py-2 border border-gray-300 text-gray-600 hover:border-fuchsia-400 hover:text-fuchsia-600 text-xs font-semibold rounded-lg transition-all cursor-pointer whitespace-nowrap"
                          >
                            View Details
                          </a>
                          <a
                            href={tool.link}
                            target="_blank"
                            rel="noopener noreferrer nofollow"
                            className="flex items-center gap-1.5 px-3.5 py-2 bg-fuchsia-500 hover:bg-fuchsia-600 text-white text-xs font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                          >
                            <i className="ri-external-link-line text-xs"></i>
                            Access
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {filtered.length === 0 && (
              <div className="text-center py-16 bg-white rounded-2xl border border-gray-200">
                <div className="w-14 h-14 flex items-center justify-center mx-auto mb-4 rounded-full bg-gray-100">
                  <i className="ri-inbox-line text-2xl text-gray-400"></i>
                </div>
                <p className="text-sm font-semibold text-gray-500">No platforms found</p>
                <p className="text-xs text-gray-400 mt-1">Try a different filter</p>
              </div>
            )}
          </div>

          {/* RELATED CATEGORIES */}
          <div className="mt-12">
            <h2 className="text-lg font-bold text-gray-900 mb-5">Related categories</h2>
            <div className="grid grid-cols-3 gap-4">
              {relatedCategories.map((cat, i) => (
                <a key={i} href={cat.href} className="bg-white rounded-2xl border border-gray-200 p-5 hover:border-fuchsia-200 transition-all cursor-pointer group">
                  <div className={`w-10 h-10 flex items-center justify-center rounded-xl bg-gradient-to-br ${cat.grad} mb-3`}>
                    <i className={`${cat.icon} text-white text-lg`}></i>
                  </div>
                  <h3 className="text-sm font-bold text-gray-900 mb-1 group-hover:text-fuchsia-600 transition-colors">{cat.name}</h3>
                  <p className="text-xs text-gray-500 leading-relaxed mb-3">{cat.desc}</p>
                  <div className="flex items-center gap-1 text-xs text-gray-400">
                    <i className="ri-tools-line"></i>
                    <span>{cat.count} tools</span>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </div>
      </main>

      {/* FOOTER */}
      <footer style={{ background: '#fce7f3' }} className="pt-14 pb-6 border-t border-gray-200">
        <div className="max-w-6xl mx-auto px-8">
          <div className="grid md:grid-cols-12 gap-10 pb-10 border-b border-gray-200">
            <div className="md:col-span-4">
              <div className="flex items-center mb-4">
                <img src="https://storage.readdy-site.link/project_files/3785ac0c-31ac-4f9e-b2ab-27ad3777db2a/7544398a-500f-41a6-81c3-aa7efbf1c0d2_logo-247.png?v=04135332188f9bc8b00780c66cd5837d" alt="247digitrend" className="h-36 w-auto object-contain" />
              </div>
              <p className="text-sm text-gray-500 leading-relaxed">
                247digitrend provides unbiased reviews and comparisons of online tools and digital platforms, helping businesses and individuals make informed decisions.
              </p>
            </div>
            <div className="md:col-span-2 md:col-start-6">
              <h5 className="text-sm font-bold text-gray-800 mb-4">Resources</h5>
              {[{ label: 'All Reviews', href: '/reviews' }, { label: 'Comparisons', href: '/comparisons' }, { label: 'Categories', href: '/categories' }, { label: 'Blog', href: '/blog' }].map((item, i) => (
                <a key={i} href={item.href} className="block text-sm text-gray-500 hover:text-fuchsia-600 transition-colors mb-3 cursor-pointer">{item.label}</a>
              ))}
            </div>
            <div className="md:col-span-2">
              <h5 className="text-sm font-bold text-gray-800 mb-4">Company</h5>
              {[{ label: 'About Us', href: '/about' }, { label: 'Contact', href: '/contact' }, { label: 'Submit a Tool', href: '/submit-tool' }, { label: 'Advertise', href: '/advertise' }].map((item, i) => (
                <a key={i} href={item.href} className="block text-sm text-gray-500 hover:text-fuchsia-600 transition-colors mb-3 cursor-pointer">{item.label}</a>
              ))}
            </div>
            <div className="md:col-span-2">
              <h5 className="text-sm font-bold text-gray-800 mb-4">Follow Us</h5>
              <div className="flex items-center gap-3">
                {['ri-twitter-x-line', 'ri-linkedin-line', 'ri-facebook-line', 'ri-instagram-line'].map((icon, i) => (
                  <a key={i} href="#" className="w-9 h-9 flex items-center justify-center rounded-full border border-gray-200 bg-white text-gray-500 hover:text-fuchsia-600 hover:border-fuchsia-300 transition-all cursor-pointer">
                    <i className={`${icon} text-base`}></i>
                  </a>
                ))}
              </div>
            </div>
          </div>
          <div className="pt-5 flex flex-col md:flex-row items-center justify-between gap-3">
            <p className="text-xs text-gray-400">© 2026 247digitrend. All rights reserved.</p>
            <div className="flex items-center gap-4">
              <a href="#" className="text-xs text-gray-400 hover:text-fuchsia-600 transition-colors cursor-pointer">Privacy Policy</a>
              <a href="#" className="text-xs text-gray-400 hover:text-fuchsia-600 transition-colors cursor-pointer">Terms of Service</a>
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-xs text-gray-400 hover:text-fuchsia-600 transition-colors cursor-pointer">Powered by Readdy</a>
            </div>
          </div>
        </div>
      </footer>

      <FloatingCtaBar onSignUp={() => setShowSignUp(true)} />
    </div>
  );
}
